import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from 'components/mui';
import { AddIcon, RemoveIcon } from 'components/icons';
import { useAddressList } from 'containers/pages/addressManagement/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import AddressFormUnified from '../addressFormUnified/AddressFormUnified';
import { Address, AddressType } from 'containers/pages/addressManagement/types';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

interface AddressListProps {
  addresses: Address[];
  addressTypes: AddressType[];
  customerId: number;
  hasCustomer: boolean;
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
}

function AddressList({ addresses, addressTypes, customerId, hasCustomer, onSave }: AddressListProps) {
  const { state, handler } = useAddressList();
  const listRef = useRef<HTMLDivElement>(null);

  const sortedAddresses = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => stableSort(addresses, getComparator(state.order, state.orderBy) as any) as Address[],
    [addresses, state.order, state.orderBy],
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedAddresses]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

      if (nearBottom && !isLoadingMore && visibleCount < sortedAddresses.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedAddresses.length));
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, sortedAddresses.length]);

  const visibleRows = useMemo(
    () => sortedAddresses.slice(0, visibleCount),
    [sortedAddresses, visibleCount],
  );

  const totalCols = state.headTableCells.length;

  const hideOnMobile = new Set(['Title']);

  return (
    <Paper ref={listRef} elevation={1}>
      {hasCustomer && (
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Showing {visibleRows.length} of {sortedAddresses.length} addresses
          </Typography>
        </Box>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCells.map((cell) => {
                const isActive = state.orderBy === cell.property;
                const direction = isActive ? state.order : 'asc';
                const isMobileHidden = hideOnMobile.has(cell.property);

                const sxProps = {
                  ...(isMobileHidden ? { display: { xs: 'none', sm: 'table-cell' } } : {}),
                  ...(cell.width ? { width: cell.width } : {}),
                };

                return (
                  <TableCell
                    key={cell.id}
                    sortDirection={isActive ? state.order : false}
                    sx={Object.keys(sxProps).length > 0 ? sxProps : undefined}
                  >
                    {cell.property ? (
                      <TableSortLabel
                        active={isActive}
                        direction={direction}
                        onClick={handler.onSort(cell.property)}
                      >
                        {cell.label}
                      </TableSortLabel>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    {!hasCustomer
                      ? 'Select a customer to continue'
                      : 'No addresses associated with this customer'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((address) => {
              const isOpen = state.selectedIds.includes(address.Id);
              return (
                <React.Fragment key={address.Id}>
                  <TableRow
                    hover
                    onClick={() => handler.onToggleExpand(address.Id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <IconButton
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        aria-expanded={isOpen}
                        size="small"
                      >
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {address.Title}
                    </TableCell>
                    <TableCell>{address.Line1}</TableCell>
                    <TableCell>{address.City}</TableCell>
                    <TableCell>{address.Province}</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={totalCols}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <AddressFormUnified
                            mode="edit"
                            addressId={address.Id}
                            customerId={customerId}
                            addressTypes={addressTypes}
                            onSave={onSave}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={totalCols}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedAddresses.length && sortedAddresses.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default AddressList;
